/**
 * Centralized password validation for Vidi.
 *
 * Enforces a single policy everywhere passwords are created or changed:
 * - Minimum 8 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one digit
 * - At least one special character (!@#$%^&* etc.)
 *
 * This utility is used client-side for immediate feedback and also imported
 * into AuthContext as a guardrail before hitting Supabase.
 *
 * Existing users are NOT affected — this only applies to new sign-ups and
 * explicit password changes.
 */

export const PASSWORD_MIN_LENGTH = 8;

export interface PasswordValidationResult {
  valid: boolean;
  error: string | null;
}

/** One password policy rule, keyed for i18n lookup by callers (see PasswordRequirements). */
export type PasswordRuleKey = 'minLength' | 'uppercase' | 'lowercase' | 'number' | 'special';

export interface PasswordRuleState {
  key: PasswordRuleKey;
  met: boolean;
}

/**
 * Validate a password against the Vidi policy.
 *
 * Returns { valid: true, error: null } on success.
 * Returns { valid: false, error: '<human-readable message>' } on failure.
 */
export function validatePassword(password: string): PasswordValidationResult {
  if (password.length < PASSWORD_MIN_LENGTH) {
    return {
      valid: false,
      error: `Password must be at least ${PASSWORD_MIN_LENGTH} characters`,
    };
  }

  if (!/[A-Z]/.test(password)) {
    return {
      valid: false,
      error: 'Password must contain at least one uppercase letter',
    };
  }

  if (!/[a-z]/.test(password)) {
    return {
      valid: false,
      error: 'Password must contain at least one lowercase letter',
    };
  }

  if (!/\d/.test(password)) {
    return {
      valid: false,
      error: 'Password must contain at least one number',
    };
  }

  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
    return {
      valid: false,
      error: 'Password must contain at least one special character',
    };
  }

  return { valid: true, error: null };
}

/**
 * Live, per-rule breakdown of password policy compliance — powers the
 * PasswordRequirements checklist UI so each rule ticks independently as the
 * user types, instead of surfacing only the first failure like
 * validatePassword does. Rule order matches validatePassword's checks.
 *
 * Does not replace validatePassword: this is presentation-only feedback,
 * validatePassword remains the single source of truth for pass/fail.
 */
export function getPasswordRuleStates(password: string): PasswordRuleState[] {
  return [
    { key: 'minLength', met: password.length >= PASSWORD_MIN_LENGTH },
    { key: 'uppercase', met: /[A-Z]/.test(password) },
    { key: 'lowercase', met: /[a-z]/.test(password) },
    { key: 'number', met: /\d/.test(password) },
    { key: 'special', met: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password) },
  ];
}
